import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Controller, useForm } from 'react-hook-form'
import { Input } from '@navent-jobs/ui-kit/components/input'
import { Select } from '@navent-jobs/ui-kit/components/select'
import { ContainerFluid, Row, Col } from '@navent-jobs/ui-kit/components/grid'
import { AlertFormData } from './ModalAlerta'
import { AlertState } from '../../../redux/types/alert-v2'
import { ParagraphSimple, CustomButton, ButtonContent } from './mixins'
import { textSanitizer } from '../../../utils/form-utils'
import { Alert } from '@navent-jobs/ui-kit/components/alert'

interface CrearAlertaFormProps {
  frequencies: AlertState['alertasFrecuencia']
  createAlertHandler: (data: AlertFormData) => void
  closeHandler: () => void
  getFrecuenciasHandler: () => void
  createAlert: AlertState['create']
}

export const CrearAlertaForm = ({
  frequencies,
  createAlertHandler,
  closeHandler,
  getFrecuenciasHandler,
  createAlert,
}: CrearAlertaFormProps) => {
  // Hooks
  const { t } = useTranslation()

  // React Hook Form
  const { control, formState, handleSubmit } = useForm({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  // Effect - Obtiene los estaticos de alertas
  useEffect(() => {
    if (!frequencies.value) {
      getFrecuenciasHandler()
    }
  }, [])

  // Handler - Submit
  const submitHandler = data => {
    const clearName = textSanitizer(data?.nombre)
    createAlertHandler({ nombre: clearName, idFrecuencia: data.frecuencia.value })
  }

  return (
    <form id="form-alert" onSubmit={handleSubmit(submitHandler)}>
      <ContainerFluid>
        {/* Titulo */}
        <Row>
          <Col>
            <ParagraphSimple>{t('alertas.form.description')}</ParagraphSimple>
          </Col>
        </Row>

        {/* Nombre */}
        <Row>
          <Col>
            <Controller
              name="nombre"
              control={control}
              rules={{
                required: true,
                validate: val => {
                  return !!val.trim()
                },
              }}
              render={({ onChange, value }) => (
                <Input
                  id="nombre"
                  name="nombre"
                  type="text"
                  value={value}
                  rules={{ required: true }}
                  onChange={onChange}
                  placeholder={t('alertas.form.nombrePlaceholder')}
                  fieldOptions={{
                    variant: 'darken',
                    label: t('alertas.form.nombreTitle'),
                    maxLength: 50,
                  }}
                />
              )}
            />
          </Col>
        </Row>

        {/* Frecuencia */}
        <Row>
          <Col>
            <Select
              name="frecuencia"
              placeholder={t('alertas.form.frecuenciaPlaceholder')}
              options={frequencies.value ?? []}
              control={control}
              rules={{ required: true }}
              fieldOptions={{
                variant: 'darken',
                label: t('alertas.form.frecuenciaTitle'),
              }}
              reactSelectOptions={{ isClearable: false, isSearchable: false }}
            />
          </Col>
        </Row>

        {/* Mensajes de error */}
        <Row>
          <Col>
            <>
              {/* Limite maximo de alertas */}
              {createAlert.value?.code === 'ALERT_LIMIT' && (
                <Alert
                  title={t('alertas.form.titleErrorLimite')}
                  link={{
                    content: t('alertas.form.linkAjustes'),
                    url: '/postulantes/notificaciones-y-alertas#alertas',
                    target: '_blank',
                  }}
                  description={t('alertas.form.errorLimite')}
                  alertOptions={{ variant: 'warning' }}
                />
              )}

              {/* Alerta duplicada */}
              {createAlert.value?.code === 'DUPLICATE_ALERT' && (
                <Alert
                  title={t('alertas.form.titleErrorDuplicada')}
                  link={{
                    content: t('alertas.form.linkAjustes'),
                    url: '/postulantes/notificaciones-y-alertas#alertas',
                    target: '_blank',
                  }}
                  description={t('alertas.form.errorDuplicada')}
                  alertOptions={{ variant: 'warning' }}
                />
              )}
            </>
          </Col>
        </Row>

        {/* Botones */}
        <Row>
          <Col>
            <ButtonContent>
              {/* Crear alerta */}
              <CustomButton
                buttonoptions={{ variant: 'primary', size: 'medium' }}
                type="submit"
                form="form-alert"
                disabled={!formState.isValid}
                order
                isLoading={createAlert.status.loading}
              >
                {t('alertas.form.buttonCrear')}
              </CustomButton>

              {/* Cancelar */}
              <CustomButton
                buttonoptions={{ size: 'medium', type: 'link', block: true }}
                onClick={e => {
                  e.preventDefault()
                  closeHandler()
                }}
              >
                {t('alertas.form.buttonCancelar')}
              </CustomButton>
            </ButtonContent>
          </Col>
        </Row>
      </ContainerFluid>
    </form>
  )
}
